import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useSetDonorLabAdmissionDecisionMutation } from '@entities/donor/api'
import { EDonorLabAdmissionDecision } from '@entities/donor/model'
import type {
  IDonorLabAdmissionDecisionPayload,
  TDonorLabAdmissionDecision,
} from '@entities/donor/model'
import { useAuthStore } from '@/shared/store/authStore'
import { getOrganizationIdFromUser } from '@/shared/utils/get-organization-id'
import { toaster } from '@/shared/ui'
import { apiFieldKeyToSnakeCase, getAxiosErrorData } from '@/shared/utils'
import type { IApiResponse } from '@/shared/types/api'
import { queryKeys } from '@/shared/api/query-keys'
import { labDeferralSchema } from './lab-admission-decision.schema'
import type { LabDeferralFormValues } from './lab-admission-decision.schema'

/** 409 — the visit is finished; no admission decision can be written to it. */
const VISIT_CLOSED_CODE = '500624'

interface Options {
  donorId: string
  /** Called after a deferral is accepted, so the caller can close its dialog. */
  onDeferralSuccess?: () => void
}

export function useLabAdmissionDecision({
  donorId,
  onDeferralSuccess,
}: Options) {
  const { t } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const schema = useMemo(() => labDeferralSchema(t), [t])

  // Which of the two buttons is waiting on the server — the other stays idle.
  const [pendingDecision, setPendingDecision] =
    useState<TDonorLabAdmissionDecision | null>(null)

  const { mutateAsync } = useSetDonorLabAdmissionDecisionMutation(donorId)
  const queryClient = useQueryClient()

  const form = useForm<LabDeferralFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '', deferral_end_date: '' },
  })

  const submit = async (payload: IDonorLabAdmissionDecisionPayload) => {
    setPendingDecision(payload.decision)
    try {
      await mutateAsync(payload)
      return true
    } catch (error) {
      const body = getAxiosErrorData<IApiResponse<null>>(error)

      // 200000 is the field-validation envelope used across the visit engine.
      const fieldErrors =
        body?.errors[0]?.code === '200000' ? body.errors[0].fields : null

      if (fieldErrors?.length) {
        fieldErrors.forEach((field) => {
          form.setError(
            apiFieldKeyToSnakeCase(field.key) as keyof LabDeferralFormValues &
              string,
            { message: field.value },
          )
        })
        return false
      }

      // This copy of the card still shows the decision buttons — reload it so
      // they go away along with the finished visit.
      if (body?.errors[0]?.code === VISIT_CLOSED_CODE) {
        toaster.error({
          title: t('donor_detail.lab_research.decision.visit_closed'),
        })
        queryClient.invalidateQueries({ queryKey: queryKeys.donorCard.all })
        return false
      }

      if (body?.errors.length) {
        body.errors.forEach((apiError) =>
          toaster.error({ title: apiError.message }),
        )
        return false
      }

      toaster.error({ title: t('donor_detail.lab_research.decision.error') })
      return false
    } finally {
      setPendingDecision(null)
    }
  }

  const admitToDonation = async () => {
    const ok = await submit({
      decision: EDonorLabAdmissionDecision.TO_DONATION,
      organization_id: getOrganizationIdFromUser(user),
    })
    if (ok)
      toaster.success({
        title: t('donor_detail.lab_research.decision.to_donation_success'),
      })
  }

  const submitDeferral = form.handleSubmit(async (data) => {
    const ok = await submit({
      decision: EDonorLabAdmissionDecision.MEDICAL_DEFERRAL,
      reason: data.reason,
      deferral_end_date: data.deferral_end_date,
      organization_id: getOrganizationIdFromUser(user),
    })
    if (!ok) return

    toaster.success({
      title: t('donor_detail.lab_research.decision.deferral_success'),
    })
    form.reset()
    onDeferralSuccess?.()
  })

  return { form, pendingDecision, admitToDonation, submitDeferral }
}
